import { useState } from 'react';
import { useSelection } from '@/app/SelectionContext';
import { usePnlSource } from '@/app/hooks';
import { PageHeader } from '@/components/PageHeader';
import { ExportMenu } from '@/components/ExportMenu';
import { Card } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { ErrorState } from '@/components/ErrorState';
import { Spinner } from '@/components/LoadingScreen';
import { NoFinancialData } from '@/components/NoFinancialData';
import { ValueDrawer } from '@/components/ValueDrawer';
import { SourceReportTable, YTD_LABEL, ytdRangeOf } from '@/components/SourceReportTable';
import { v } from '@/theme/tokens';

export function PnLPage() {
  const { selectedUnit, selectedPeriod } = useSelection();
  const year = selectedPeriod?.year ?? new Date().getFullYear();
  const query = usePnlSource(selectedUnit?.id, year);
  const [drawer, setDrawer] = useState<{ categoryId: string; name: string } | null>(null);

  const data = query.data;
  const periods = data?.periods ?? [];
  const periodKey = selectedPeriod
    ? `${selectedPeriod.year}-${String(selectedPeriod.month).padStart(2, '0')}`
    : null;
  const ytdRange = ytdRangeOf(periods);

  const notFound =
    query.error instanceof Error && /no_confirmed_import|404/.test(query.error.message);

  return (
    <div style={{ animation: 'fade-in 0.35s ease' }}>
      <PageHeader
        title="Отчёт о прибылях и убытках"
        subtitle={
          data
            ? `${selectedUnit?.name ?? data.sheet_name} · ${year} · из файла ${data.filename} (ревизия ${data.revision})`
            : selectedUnit
              ? `${selectedUnit.name} · ${year}`
              : String(year)
        }
        actions={
          <>
            {selectedPeriod?.status === 'CLOSED' && <Badge tone="positive">● Закрыт</Badge>}
            {selectedUnit && (
              <ExportMenu report="pnl" params={{ businessUnitId: selectedUnit.id, year }} />
            )}
          </>
        }
      />

      {query.isPending && <Spinner />}

      {query.error && !notFound && (
        <ErrorState
          message={query.error instanceof Error ? query.error.message : 'Ошибка загрузки'}
          onRetry={() => void query.refetch()}
        />
      )}

      {notFound && <NoFinancialData unitName={selectedUnit?.name} />}

      {data && data.rows.length === 0 && <NoFinancialData unitName={selectedUnit?.name} />}

      {data && data.rows.length > 0 && (
        <Card>
          <SourceReportTable
            rows={data.rows}
            periods={periods}
            selectedKey={periodKey}
            onOpenValue={(categoryId, name) => setDrawer({ categoryId, name })}
          />
          <div style={{ fontSize: 12, color: v('ink-muted'), marginTop: 10 }}>
            Отчёт показан как в подтверждённом файле «{data.filename}»: строки, коды статей и значения —
            из Excel, без пересчёта. Значения компактно (тыс./млн). Колонка «{YTD_LABEL}» суммируется из
            показанных месяцев ({ytdRange}) — годовая формула файла не используется. Для процентных строк
            итог не выводится. Клик по статье — источник, версии, корректировки и комментарии.
          </div>
        </Card>
      )}

      {drawer && selectedUnit && selectedPeriod && (
        <ValueDrawer
          unitId={selectedUnit.id}
          categoryId={drawer.categoryId}
          categoryName={drawer.name}
          periodId={selectedPeriod.id}
          periodClosed={selectedPeriod.status === 'CLOSED'}
          onClose={() => setDrawer(null)}
        />
      )}
    </div>
  );
}
